import { isEmail2 } from "helpers/validator";

// The backend serialises invoice-delivery contact emails into a single
// comma-separated string. Reject payloads whose combined length exceeds this
// limit and ask the user to remove some entries.
export const MAX_INVOICE_DELIVERY_EMAILS_LENGTH = 1200;

// Lower-case and trim a single email so stored values are always normalised.
export const normaliseEmail = (email: string): string =>
  email.trim().toLowerCase();

// Returns the non-empty, normalised emails from a list of raw row values.
const nonEmptyEmails = (emails: string[]): string[] =>
  emails.map(normaliseEmail).filter((email) => email.length > 0);

// True if the list contains duplicate (case-insensitive) emails. Blank rows are ignored.
export const hasDuplicateEmails = (emails: string[]): boolean => {
  const filled = nonEmptyEmails(emails);
  return new Set(filled).size !== filled.length;
};

// Indices of rows whose (normalised, non-empty) email already appeared in an
// earlier row — i.e. the redundant duplicate entries. The first occurrence and
// all blank rows are never included.
export const duplicateEmailIndices = (emails: string[]): Set<number> => {
  const seen = new Set<string>();
  const duplicates = new Set<number>();
  emails.forEach((raw, index) => {
    const email = normaliseEmail(raw);
    if (!email) return;
    if (seen.has(email)) duplicates.add(index);
    else seen.add(email);
  });
  return duplicates;
};

// True if any non-empty email is not a valid email address.
export const hasInvalidEmails = (emails: string[]): boolean =>
  nonEmptyEmails(emails).some((email) => !isEmail2(email));

// Total length of the "email," segments for every non-empty email — mirrors the
// comma-separated string the backend stores (each email contributes its own comma).
export const invoiceDeliveryEmailsLength = (emails: string[]): number =>
  nonEmptyEmails(emails).reduce((sum, email) => sum + email.length + 1, 0);

// True when the combined email length exceeds the allowed maximum.
export const exceedsEmailsLengthLimit = (emails: string[]): boolean =>
  invoiceDeliveryEmailsLength(emails) > MAX_INVOICE_DELIVERY_EMAILS_LENGTH;
